import type { EventHookOn } from '@vueuse/core';
import { getCurrentScope, onScopeDispose, type Ref } from 'vue';
import { dataLayerTracker } from '@/entities/analytics';
import type { ProductSearch, ProductSearchEvents } from '@/entities/product';
import type { SearchCriteria } from '@/entities/searchCriteria';
import type { FiltersModel, SerpFilterInteraction, SerpProduct, SerpSort } from './types';
import {
  SERP_DATA_LAYER_BANNERS_STEP,
  SERP_DATA_LAYER_FILTER_PAGES,
  SERP_DATA_LAYER_MAX_STEP,
  SERP_DATA_LAYER_SORT_VALUES,
} from '../config/dataLayer';
import {
  getSerpDataLayerCriteria,
  getSerpFilterDataLayer,
  getSerpSearchClick,
  getSerpTopHotelIds,
} from '../lib/serpDataLayer';

interface SerpDataLayerFilters {
  filtersModel: Readonly<Ref<FiltersModel>>;
  onFilterInteraction: EventHookOn<SerpFilterInteraction>;
}

interface SerpDataLayerOptions {
  filters: SerpDataLayerFilters;
  sort: Readonly<Ref<SerpSort>>;
}

export interface SerpDataLayer {
  trackSearchStart(criteria: SearchCriteria, searchId: string): void;
  trackFiltersOpen(): void;
  trackHotelClick(serpProduct: SerpProduct, position: number): void;
  trackSortSelect(sort: SerpSort): void;
  trackFiltersApplied(isExclusive: boolean): void;
}

function skipTracking(): void {
  return undefined;
}

const DISABLED_SERP_DATA_LAYER: SerpDataLayer = {
  trackSearchStart: skipTracking,
  trackFiltersOpen: skipTracking,
  trackHotelClick: skipTracking,
  trackSortSelect: skipTracking,
  trackFiltersApplied: skipTracking,
};

function trackFilterInteraction(interaction: SerpFilterInteraction): void {
  const filterDataLayer = getSerpFilterDataLayer(interaction);

  if (!filterDataLayer) return;

  if (filterDataLayer.sideBar) dataLayerTracker.sideBarFilter(filterDataLayer.sideBar);

  dataLayerTracker.toggleFilter(filterDataLayer.checked, filterDataLayer.value);
}

export function useSerpDataLayer(productSearch: ProductSearch, { filters, sort }: SerpDataLayerOptions): SerpDataLayer {
  const filterPage = SERP_DATA_LAYER_FILTER_PAGES[productSearch.type];

  if (!filterPage) return DISABLED_SERP_DATA_LAYER;

  let trackedSearchId: string | null = null;

  dataLayerTracker.searchPage(filterPage, SERP_DATA_LAYER_MAX_STEP);

  const onEnd = (result: ProductSearchEvents['end']): void => {
    if (result.state !== 'finished') return;

    dataLayerTracker.searchCount(result.hotels.length);
    dataLayerTracker.search(
      getSerpTopHotelIds(result, filters.filtersModel.value, sort.value),
      SERP_DATA_LAYER_BANNERS_STEP,
    );
  };

  const unsubscribe = productSearch.on('end', onEnd);

  filters.onFilterInteraction(trackFilterInteraction);

  if (getCurrentScope()) onScopeDispose(unsubscribe);

  return {
    trackSearchStart(criteria: SearchCriteria, searchId: string): void {
      if (searchId === trackedSearchId) return;

      trackedSearchId = searchId;
      dataLayerTracker.searchCriteria(searchId, getSerpDataLayerCriteria(criteria));
    },
    trackFiltersOpen(): void {
      dataLayerTracker.openFilters();
    },
    trackHotelClick(serpProduct: SerpProduct, position: number): void {
      const click = getSerpSearchClick(serpProduct, position);

      if (click) dataLayerTracker.searchClick(click);
    },
    trackSortSelect(value: SerpSort): void {
      dataLayerTracker.toggleFilter(true, SERP_DATA_LAYER_SORT_VALUES[value]);
    },
    trackFiltersApplied(isExclusive: boolean): void {
      dataLayerTracker.filtersApplied(isExclusive, filterPage);
    },
  };
}
